import React from "react";
import {
  FiServer, FiWifi, FiActivity, FiAlertTriangle, FiCpu,
  FiCheckCircle, FiXCircle, FiAlertOctagon
} from "react-icons/fi";
import { MdSettingsInputAntenna } from "react-icons/md";
import { allDevices, deviceDummyData } from "../../../Data/DeviceData";
import { healthColor, healthLabel, HealthRing, StatusBadge, CONN_META, connMeta } from "./deviceHelpers";

const SOLUTIONS = ["STP", "AQM", "Irrigation", "Facility"];

const SOL_COLORS = {
  STP:        { color: "#3b82f6", bg: "#dbeafe" },
  AQM:        { color: "#8b5cf6", bg: "#ede9fe" },
  Irrigation: { color: "#10b981", bg: "#d1fae5" },
  Facility:   { color: "#f97316", bg: "#ffedd5" },
};

const DvcOverviewPanel = ({ d }) => {
  const all = allDevices;
  const gateways    = d.gateways;
  const sensors     = d.sensors;
  const controllers = d.controllers;

  const online   = all.filter(x => x.status === "Online").length;
  const offline  = all.filter(x => x.status === "Offline").length;
  const warning  = all.filter(x => x.status === "Warning").length;
  const error    = all.filter(x => x.status === "Error").length;
  const unhealthy = all.filter(x => x.healthScore < 60).length;
  const critAlerts = d.alerts.filter(a => a.severity === "Critical" && !a.ack).length;

  // Connectivity distribution
  const connCounts = {};
  all.forEach(dv => { connCounts[dv.connectivity] = (connCounts[dv.connectivity] || 0) + 1; });

  // Solution distribution
  const solCounts = {};
  SOLUTIONS.forEach(s => { solCounts[s] = all.filter(x => x.assignedSolution === s).length; });

  // Health distribution
  const healthDist = [
    { label: "Healthy ≥85",  count: all.filter(x => x.healthScore >= 85).length,  color: "#10b981" },
    { label: "Fair 60–84",   count: all.filter(x => x.healthScore >= 60 && x.healthScore < 85).length, color: "#f59e0b" },
    { label: "Poor 30–59",   count: all.filter(x => x.healthScore >= 30 && x.healthScore < 60).length, color: "#f97316" },
    { label: "Critical <30", count: all.filter(x => x.healthScore < 30).length,   color: "#ef4444" },
  ];
  const maxHealth = Math.max(...healthDist.map(x => x.count), 1);

  const kpis = [
    { label: "Total Devices",   val: all.length,       color: "#3b82f6", icon: FiServer },
    { label: "Online",          val: online,            color: "#10b981", icon: FiCheckCircle },
    { label: "Offline",         val: offline,           color: "#6b7280", icon: FiXCircle },
    { label: "Warning / Error", val: warning + error,   color: "#f59e0b", icon: FiAlertTriangle },
    { label: "Unhealthy (<60)", val: unhealthy,         color: "#ef4444", icon: FiAlertOctagon },
    { label: "Gateways",        val: gateways.length,   color: "#667eea", icon: MdSettingsInputAntenna },
    { label: "Sensors",         val: sensors.length,    color: "#10b981", icon: FiActivity },
    { label: "Controllers",     val: controllers.length, color: "#f97316", icon: FiCpu },
  ];

  return (
    <div className="dvc-overview">
      {/* KPI Grid */}
      <div className="dvc-kpi-grid">
        {kpis.map(k => (
          <div key={k.label} className="dvc-kpi-card" style={{ borderTopColor: k.color }}>
            <div className="dvc-kpi-icon" style={{ background: k.color + "18", color: k.color }}>
              <k.icon size={18} />
            </div>
            <div className="dvc-kpi-val" style={{ color: k.color }}>{k.val}</div>
            <div className="dvc-kpi-lbl">{k.label}</div>
          </div>
        ))}
      </div>

      <div className="dvc-ov-row">
        {/* Health Distribution */}
        <div className="dvc-ov-card">
          <div className="dvc-section-title">Device Health Distribution</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
            {healthDist.map(h => (
              <div key={h.label} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 120, fontSize: 12, color: "#374151", fontWeight: 500 }}>{h.label}</div>
                <div style={{ flex: 1, height: 14, background: "#f1f5f9", borderRadius: 7, overflow: "hidden" }}>
                  <div style={{ width: `${(h.count / maxHealth) * 100}%`, height: "100%", background: h.color, borderRadius: 7, transition: "width 0.5s" }} />
                </div>
                <div style={{ width: 24, textAlign: "right", fontWeight: 700, color: h.color, fontSize: 13 }}>{h.count}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16 }}>
            <div className="dvc-section-title" style={{ fontSize: 12 }}>Device Health Scores</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 8 }}>
              {all.map(dv => (
                <div key={dv.id} title={`${dv.name}: ${dv.healthScore} — ${healthLabel(dv.healthScore)}`}
                  style={{ width: 36, height: 36, borderRadius: "50%", background: healthColor(dv.healthScore) + "22",
                    border: `2px solid ${healthColor(dv.healthScore)}`, display: "flex", alignItems: "center",
                    justifyContent: "center", fontSize: 10, fontWeight: 700, color: healthColor(dv.healthScore), cursor: "default" }}>
                  {dv.healthScore}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Connectivity Breakdown */}
        <div className="dvc-ov-card">
          <div className="dvc-section-title">Connectivity Breakdown</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
            {Object.entries(connCounts).map(([conn, cnt]) => {
              const m = connMeta(conn);
              const pct = (cnt / all.length) * 100;
              return (
                <div key={conn} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <m.Icon size={15} color={m.color} style={{ flexShrink: 0 }} />
                  <div style={{ width: 80, fontSize: 12, color: "#374151", fontWeight: 500 }}>{m.label}</div>
                  <div style={{ flex: 1, height: 14, background: "#f1f5f9", borderRadius: 7, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: m.color, borderRadius: 7, transition: "width 0.5s" }} />
                  </div>
                  <div style={{ width: 24, textAlign: "right", fontWeight: 700, color: m.color, fontSize: 13 }}>{cnt}</div>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 20 }}>
            <div className="dvc-section-title">Assigned Solutions</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
              {SOLUTIONS.map(s => {
                const sc = SOL_COLORS[s];
                return (
                  <div key={s} className="dvc-sol-chip" style={{ background: sc.bg, color: sc.color, borderColor: sc.color }}>
                    <span style={{ fontSize: 18, fontWeight: 800 }}>{solCounts[s]}</span>
                    <span style={{ fontSize: 11 }}>{s}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Status Summary + Alerts */}
        <div className="dvc-ov-card">
          <div className="dvc-section-title">Status Summary</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
            {[
              { label: "Online",   count: online,  color: "#10b981", icon: FiCheckCircle },
              { label: "Warning",  count: warning, color: "#f59e0b", icon: FiAlertTriangle },
              { label: "Error",    count: error,   color: "#ef4444", icon: FiAlertOctagon },
              { label: "Offline",  count: offline, color: "#6b7280", icon: FiXCircle },
            ].map(s => (
              <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                background: s.color + "10", borderRadius: 8, border: `1px solid ${s.color}22` }}>
                <s.icon size={16} color={s.color} />
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#374151" }}>{s.label}</span>
                <span style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.count}</span>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 16 }}>
            <div className="dvc-section-title">Active Alerts</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
              {d.alerts.filter(a => !a.ack).slice(0, 4).map(a => (
                <div key={a.id} style={{ padding: "6px 10px", background: a.severity === "Critical" ? "#fee2e2" : "#fef3c7",
                  borderLeft: `3px solid ${a.severity === "Critical" ? "#ef4444" : "#f59e0b"}`,
                  borderRadius: 6, fontSize: 11, color: "#374151" }}>
                  <div style={{ fontWeight: 700, color: a.severity === "Critical" ? "#ef4444" : "#f59e0b" }}>{a.deviceName}</div>
                  <div style={{ marginTop: 2 }}>{a.msg.length > 60 ? a.msg.slice(0, 60) + "…" : a.msg}</div>
                </div>
              ))}
              {critAlerts === 0 && (
                <div style={{ textAlign: "center", color: "#10b981", fontSize: 13, fontWeight: 600, padding: 8 }}>
                  ✅ No critical alerts
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DvcOverviewPanel;
